export class AuthService {
  constructor(apiClient, errorHandler) {
    this.apiClient = apiClient;
    this.handleApiError = errorHandler;
  }

  async register(userData) {
    try {
      const response = await this.apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw this.handleApiError(error);
    }
  }

  async login(credentials) {
    try {
      // FastAPI OAuth2PasswordRequestForm requires application/x-www-form-urlencoded
      // (not multipart/form-data — URLSearchParams triggers the correct encoding in axios)
      const formData = new URLSearchParams();
      formData.append('username', credentials.email); // FastAPI OAuth2 uses 'username' field
      formData.append('password', credentials.password);
      formData.append('remember_me', credentials.rememberMe ? 'true' : 'false');

      const response = await this.apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      // Cookie is set by the backend — nothing to store on the client
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw this.handleApiError(error);
    }
  }

  async logout() {
    try {
      await this.apiClient.post('/auth/logout');
    } catch (error) {
      // Best-effort — cookie will expire naturally if request fails
    }
  }

  async getCurrentUser() {
    try {
      const response = await this.apiClient.get('/users/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw this.handleApiError(error);
    }
  }

  async updateUserProfile(profileData) {
    try {
      const response = await this.apiClient.put('/users/me', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw this.handleApiError(error);
    }
  }

  async uploadProfilePicture(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.apiClient.post('/users/me/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw this.handleApiError(error);
    }
  }

  async changePassword(passwordData) {
    try {
      const response = await this.apiClient.put('/users/me/password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw this.handleApiError(error);
    }
  }

  async deleteAccount() {
    try {
      const response = await this.apiClient.delete('/users/me');
      return response.data;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw this.handleApiError(error);
    }
  }

  async forgotPassword(email) {
    try {
      const response = await this.apiClient.post('/auth/password-reset', { email });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw this.handleApiError(error);
    }
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await this.apiClient.post('/auth/password-reset-confirm', {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw this.handleApiError(error);
    }
  }

  async validateResetToken(token) {
    try {
      const response = await this.apiClient.post('/auth/validate-reset-token', {
        token
      });
      return response.data;
    } catch (error) {
      console.error('Error validating reset token:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Auth state is managed by the cookie — the store determines
   * authentication by whether a user was loaded successfully.
   */
  isAuthenticated() {
    return false; // Stub — use authStore.isAuthenticated instead
  }

  getAuthToken() {
    return null; // Token lives in httpOnly cookie — not accessible to JS
  }

  async resendVerificationEmail(email) {
    try {
      const response = await this.apiClient.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw this.handleApiError(error);
    }
  }

  async verifyEmail(token) {
    try {
      const response = await this.apiClient.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying email:', error);
      throw this.handleApiError(error);
    }
  }
}

export default AuthService;